import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { BoldFont, MediumFont } from "../common/Style";
import Register from "./Register";
import GenericModal from "../common/modals/GenericModal";
import OpacityLoading from "../common/loader/OpacityLoading";
import { CFV, copyToClipBoard } from "../common/others/Functions";
import { login } from "../../store/reducers/authReducer";
import bsLogo from "../../assets/common/comps/bsLogo.png";
import personIcon from "../../assets/common/person.svg";

// ——— פלטת צבעים מהגרדיאנט של סקשן הלוגו (צבעים חיים) ———
const COLORS = {
  heroDark: "#0f0c29",
  heroMid: "#302b63",
  heroBottom: "#24243e",
  heroGradient:
    "linear-gradient(180deg, #0f0c29 0%, #302b63 38%, #24243e 100%)",
  bg: "#f4f1fc",
  bgSection: "#fdfcff",
  text: "#1e1a38",
  textSecondary: "#4a4569",
  accent: "#5045c2",
  accentLight: "#7a6bdd",
  border: "#ddd6f0",
  cardHover: "#ebe7f8",
  muted: "#78719a",
};

const DEFAULT_UNAUTH_STORAGE_KEY = "defaultUnAuthRoute";

const SBS_LOGIN_URL = "https://callup.ltd/v/besimple/index.html";

const sbsWelcomeBackdrop = {
  position: "fixed",
  top: 0,
  left: 0,
  right: 0,
  bottom: 0,
  background: "rgba(0,0,0,0.45)",
  zIndex: 1200,
};

const sbsWelcomeBox = {
  position: "fixed",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: "90%",
  maxWidth: 400,
  background: COLORS.bgSection,
  borderRadius: 16,
  boxShadow: `0 12px 40px ${COLORS.accent}40, 0 0 0 1px rgba(0,0,0,0.05)`,
  padding: "24px 22px",
  zIndex: 1201,
  direction: "rtl",
  textAlign: "center",
};

const sbsWelcomeTitle = {
  margin: "0 0 16px",
  fontSize: 24,
  fontWeight: 700,
  color: COLORS.accent,
  lineHeight: 1.2,
};

const sbsWelcomeIntro = {
  margin: "0 0 12px",
  fontSize: 15,
  fontWeight: 600,
  color: COLORS.text,
};

const sbsWelcomeDetails = {
  margin: "0 0 14px",
  padding: "14px 16px",
  background: `${COLORS.accent}14`,
  borderRadius: 12,
  textAlign: "right",
};

const sbsWelcomeRow = {
  margin: "0 0 8px",
  fontSize: 14,
  color: COLORS.text,
  lineHeight: 1.5,
};

const sbsWelcomeLink = {
  color: COLORS.accent,
  fontWeight: 600,
  wordBreak: "break-all",
};

const sbsWelcomeNote = {
  margin: "0 0 12px",
  fontSize: 13,
  color: COLORS.textSecondary,
  lineHeight: 1.5,
};

const sbsWelcomeCheers = {
  margin: "0 0 18px",
  fontSize: 16,
  fontWeight: 600,
  color: COLORS.text,
};

const sbsWelcomeCloseBtn = {
  padding: "10px 28px",
  fontSize: 15,
  fontWeight: 600,
  color: "#ffffff",
  background: COLORS.heroGradient,
  border: "none",
  borderRadius: 12,
  cursor: "pointer",
  boxShadow: `0 4px 16px ${COLORS.heroDark}40`,
};

const sbsWelcomeCopyBtn = {
  width: "100%",
  marginBottom: 14,
  padding: "12px 16px",
  fontSize: 15,
  fontFamily: "hebM",
  fontWeight: 600,
  color: COLORS.accent,
  background: "#ffffff",
  border: `2px solid ${COLORS.accent}`,
  borderRadius: 12,
  cursor: "pointer",
  boxSizing: "border-box",
};

// ——— סקשן 1: לוגו + 2 שורות ———
const SectionHero = () => (
  <section
    style={{
      background: COLORS.heroGradient,
      padding: "60px clamp(16px, 5vw, 32px) 80px",
      textAlign: "center",
      borderBottom: "1px solid rgba(255,255,255,0.1)",
      width: "100%",
      boxSizing: "border-box",
    }}
  >
    <img
      src={bsLogo}
      alt="Be Simple Events"
      style={{
        width: "min(208px, 85vw)",
        maxWidth: 208,
        height: "auto",
        margin: "0 auto 24px",
        display: "block",
        objectFit: "contain",
      }}
    />
    <BoldFont
      style={{
        fontSize: 28,
        color: "#ffffff",
        marginBottom: 12,
        lineHeight: 1.4,
      }}
    >
      אישורי הגעה וסידורי הושבה לאירועים
    </BoldFont>
    <MediumFont
      style={{ fontSize: 17, color: "#ffffff", lineHeight: 1.5, opacity: 0.95 }}
    >
      המערכת שתלווה אתכם בתהליך מסודר, נוח ופשוט!
    </MediumFont>
  </section>
);

// ——— סקשן 2: 3 חבילות + רוביקות ———
const FEATURES = [
  { key: "sms", label: "2 הודעות SMS" },
  { key: "whatsapp", label: "3 הודעות Whatsapp" },
  { key: "calls", label: "4 סבבי שיחות אנושיות" },
  {
    key: "seating",
    label: "הושבה דיגיטלית SMS + Whatsapp",
    labelSmsOnly: "הושבה דיגיטלית ב SMS",
  },
  {
    key: "reminder_days",
    label:
      "תזכורת כמה ימים לפני החתונה לרענן לכולם את הזיכרון ולשאול האם חל שינוי SMS",
  },
  {
    key: "reminder_morning",
    label: "תזכורת בוקר האירוע ב SMS",
  },
  {
    key: "reminder_morning_whatsapp",
    label: "תזכורת בוקר האירוע ב Whatsapp",
  },
  // { key: "reminder_evening", label: "תזכורת נוספת ערב האירוע" },
  { key: "thank_you", label: "הודעת תודה למחרת SMS" },
  { key: "customer_service", label: "שירות לקוחות" },
];

const PACKAGES = [
  {
    id: 1,
    name: "בסיסי",
    priceLabel: "0.25₪ לרשומה",
    disabled: [
      "whatsapp",
      "calls",
      "customer_service",
      "reminder_morning_whatsapp",
    ],
  },
  {
    id: 2,
    name: "מתקדם",
    priceLabel: "0.65₪ לרשומה",
    disabled: ["calls", "customer_service"],
  },
  {
    id: 3,
    name: "פרימיום",
    priceLabel: "1.25₪ לרשומה",
    disabled: ["customer_service"],
  },
  { id: 4, name: "VIP", priceLabel: "1.5₪ לרשומה", disabled: [] },
];

const SectionPackages = () => {
  const [hovered, setHovered] = useState(null);
  const [showRecordExplain, setShowRecordExplain] = useState(false);
  const [showCreditGift, setShowCreditGift] = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [welcomePopupOpen, setWelcomePopupOpen] = useState(false);
  const [welcomePhone, setWelcomePhone] = useState("");
  const [welcomePassword, setWelcomePassword] = useState("");

  const handleRegisterClick = (pkg) => {
    setSelectedPackage(pkg);
    setShowRegister(true);
  };

  const handleRegisterSbsSuccess = ({ phone, password }) => {
    setShowRegister(false);
    setWelcomePhone(phone);
    setWelcomePassword(password);
    setWelcomePopupOpen(true);
  };

  return (
    <section
      style={{
        background: COLORS.bgSection,
        padding: "64px clamp(16px, 5vw, 32px) 80px",
        borderBottom: `1px solid ${COLORS.border}`,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <BoldFont
        style={{
          fontSize: "clamp(20px, 5vw, 24px)",
          color: COLORS.heroDark,
          textAlign: "center",
          marginBottom: 24,
        }}
      >
        החבילות שלנו
      </BoldFont>

      {/* כפתורי הסבר — מה זה רשומה + מתנות באשראי */}
      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "center",
          gap: 12,
          marginBottom: 40,
        }}
      >
        <button
          type="button"
          onClick={() => setShowRecordExplain(true)}
          style={{
            padding: "12px 28px",
            borderRadius: 10,
            border: "none",
            background: COLORS.heroGradient,
            color: "#ffffff",
            fontSize: 16,
            fontFamily: "hebM",
            cursor: "pointer",
            boxShadow: `0 4px 16px ${COLORS.heroDark}50`,
          }}
        >
          מה זה רשומה?
        </button>
        <button
          type="button"
          onClick={() => setShowCreditGift(true)}
          style={{
            padding: "12px 28px",
            borderRadius: 10,
            border: "none",
            background: COLORS.heroGradient,
            color: "#ffffff",
            fontSize: 16,
            fontFamily: "hebM",
            cursor: "pointer",
            boxShadow: `0 4px 16px ${COLORS.heroDark}50`,
          }}
        >
          מתנות באשראי
        </button>
      </div>

      <div
        style={{
          display: "flex",
          flexWrap: "wrap",
          gap: 24,
          justifyContent: "center",
          maxWidth: 1000,
          width: "100%",
          margin: "0 auto",
          boxSizing: "border-box",
        }}
      >
        {PACKAGES.map((p) => (
          <div
            key={p.id}
            style={{
              flex: "1 1 260px",
              minWidth: 0,
              maxWidth: 320,
              padding: "24px clamp(16px, 4vw, 24px)",
              borderRadius: 16,
              border: `1px solid ${COLORS.border}`,
              background: COLORS.bgSection,
              transition: "box-shadow 0.2s, transform 0.2s",
              cursor: "default",
              boxShadow:
                hovered === p.id
                  ? `0 10px 32px ${COLORS.accent}18`
                  : `0 2px 12px ${COLORS.border}`,
              transform: hovered === p.id ? "translateY(-2px)" : "none",
            }}
            onMouseEnter={() => setHovered(p.id)}
            onMouseLeave={() => setHovered(null)}
          >
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "baseline",
                flexWrap: "wrap",
                gap: 8,
                marginBottom: 20,
              }}
            >
              <BoldFont style={{ fontSize: 20, color: COLORS.text }}>
                {p.name}
              </BoldFont>
              <BoldFont style={{ fontSize: 18, color: COLORS.accent }}>
                {p.priceLabel}
              </BoldFont>
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {FEATURES.map((f) => {
                const isDisabled = p.disabled.includes(f.key);
                const isSmsOnly = p.disabled.includes("whatsapp");
                const displayLabel =
                  isSmsOnly && f.labelSmsOnly ? f.labelSmsOnly : f.label;
                return (
                  <div
                    key={f.key}
                    style={{
                      padding: "10px 14px",
                      borderRadius: 10,
                      background: isDisabled ? COLORS.cardHover : "#ffffff",
                      border: `1px solid ${isDisabled ? COLORS.border : COLORS.border}`,
                      boxShadow: isDisabled
                        ? "none"
                        : `0 2px 8px ${COLORS.accent}12`,
                    }}
                  >
                    <MediumFont
                      style={{
                        fontSize: 14,
                        color: isDisabled ? COLORS.muted : COLORS.text,
                        lineHeight: 1.4,
                        textDecoration: isDisabled ? "line-through" : "none",
                        opacity: isDisabled ? 0.85 : 1,
                      }}
                    >
                      {displayLabel}
                    </MediumFont>
                  </div>
                );
              })}
            </div>
            <button
              type="button"
              onClick={() => handleRegisterClick(p)}
              style={{
                width: "100%",
                marginTop: 20,
                padding: "12px 20px",
                border: "none",
                borderRadius: 10,
                background: COLORS.heroGradient,
                color: "#ffffff",
                fontSize: 15,
                fontFamily: "hebB",
                cursor: "pointer",
                boxShadow: `0 4px 12px ${COLORS.heroDark}40`,
                transition: "transform 0.2s, box-shadow 0.2s",
              }}
            >
              הרשמה
            </button>
          </div>
        ))}
      </div>

      {showRecordExplain && (
        <GenericModal
          div={
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: COLORS.bgSection,
                padding: 28,
                borderRadius: 16,
                maxWidth: "min(420px, calc(100vw - 32px))",
                width: "100%",
                maxHeight: "85vh",
                overflowY: "auto",
                boxShadow: `0 12px 40px ${COLORS.accent}25`,
              }}
            >
              <BoldFont
                style={{
                  fontSize: 20,
                  color: COLORS.text,
                  marginBottom: 14,
                  display: "block",
                }}
              >
                רשומה לעומת אורח
              </BoldFont>
              <MediumFont
                style={{ fontSize: 15, color: COLORS.text, lineHeight: 1.75 }}
              >
                <span style={{ display: "block", marginBottom: 12 }}>
                  נניח שיש לנו דוד נשוי פלוס 2 ילדים בני 10, לכאורה מדובר ב־4
                  אורחים אך רק רשומה אחת מכיוון שהדוד עונה עבור כולם. לכן, כמות
                  הרשומות נמוכה בהרבה מכמות המוזמנים.
                </span>
                <span style={{ display: "block", marginBottom: 12 }}>
                  נקודה חשובה נוספת — התשלום הינו רק עבור רשומות שיש לוודא איתן
                  הגעה. משמע: רשומות כמו סבים, סבתות, משפחה גרעינית, מלווים
                  ובכלל אנשים שמבחינתכם מגיעים בוודאות ואין לשלוח להם הודעה
                  ולהתקשר — תוכלו להוסיף למערכת ולא לשלם עליהם. עדיין יקבלו
                  הודעת תזכורת, מספר שולחן והודעת תודה.
                </span>
                <span style={{ display: "block" }}>
                  רק אחרי שתטענו למערכת את כלל המוזמנים, המערכת תסכום את מספר
                  הרשומות שיש לוודא לגביהן הגעה — ועל פי מספר זה ייגבה התשלום.
                </span>
              </MediumFont>
              <button
                type="button"
                onClick={() => setShowRecordExplain(false)}
                style={{
                  marginTop: 20,
                  padding: "10px 24px",
                  border: "none",
                  borderRadius: 8,
                  background: COLORS.accentLight,
                  color: COLORS.bgSection,
                  fontFamily: "hebM",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                סגור
              </button>
            </div>
          }
          init={() => setShowRecordExplain(false)}
        />
      )}

      {showCreditGift && (
        <GenericModal
          div={
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: COLORS.bgSection,
                padding: 28,
                borderRadius: 16,
                maxWidth: "min(460px, calc(100vw - 32px))",
                width: "100%",
                maxHeight: "85vh",
                overflowY: "auto",
                boxShadow: `0 12px 40px ${COLORS.accent}25`,
              }}
            >
              <BoldFont
                style={{
                  fontSize: 20,
                  color: COLORS.text,
                  marginBottom: 14,
                  display: "block",
                }}
              >
                השירות החשוב ביותר
              </BoldFont>
              <MediumFont
                style={{ fontSize: 15, color: COLORS.text, lineHeight: 1.75 }}
              >
                <span style={{ display: "block", marginBottom: 12 }}>
                  הרי לא כל המוזמנים יגיעו, נכון יהיו את אלה שיעבירו מתנה בכל
                  זאת אבל יש הרבה שלא...
                </span>
                <span style={{ display: "block", marginBottom: 12 }}>
                  ביום האירוע תישלח הודעת תזכורת לכלל האורחים עם האופציה למתנה
                  באשראי (גם עבור אורחים שלא יגיעו). סביר מאוד שיהיו אורחים
                  שבסוף כן ישלחו מתנה בעקבות ההודעה מתוך כבוד, כאשר רואים את
                  האופציה בהודעה מול העיניים.
                </span>
                <span style={{ display: "block", marginBottom: 12 }}>
                  הרבה זוגות שלא רצו בהתחלה והשתכנעו הודו לנו לאחר החתונה — ראו
                  את רשימות האורחים שנתנו מתנה באשראי ואמרו בפירוש שלא בטוח היו
                  שולחים בדרך אחרת.
                </span>
                <span style={{ display: "block", marginBottom: 12 }}>
                  אין אירוע שלא לוקח את השירות הזה — אל תחסירו מהאורח את האופציה
                  הזו! מה גם שזה נוח גם עבור אורחים שכן מגיעים לאירוע, במקום
                  לעצור להוציא מזומן.
                </span>
                <span
                  style={{
                    display: "block",
                    fontSize: 14,
                    color: COLORS.textSecondary,
                    fontStyle: "italic",
                  }}
                >
                  הוספת שירות זה ניתן לאחר פתיחת משתמש והעלאת ההזמנה לאתר.
                </span>
              </MediumFont>
              <button
                type="button"
                onClick={() => setShowCreditGift(false)}
                style={{
                  marginTop: 20,
                  padding: "10px 24px",
                  border: "none",
                  borderRadius: 8,
                  background: COLORS.accentLight,
                  color: COLORS.bgSection,
                  fontFamily: "hebM",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                סגור
              </button>
            </div>
          }
          init={() => setShowCreditGift(false)}
        />
      )}

      {showRegister && (
        <GenericModal
          div={
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: COLORS.bgSection,
                padding: "32px 24px",
                borderRadius: 20,
                maxWidth: "min(400px, calc(100vw - 32px))",
                width: "100%",
                maxHeight: "85vh",
                overflowY: "auto",
                boxShadow: `0 12px 40px ${COLORS.accent}30`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <BoldFont
                style={{
                  fontSize: 22,
                  color: COLORS.text,
                  marginBottom: 8,
                  textAlign: "center",
                }}
              >
                הרשמה לחבילת {selectedPackage?.name}
              </BoldFont>
              <MediumFont
                style={{
                  fontSize: 14,
                  color: COLORS.textSecondary,
                  marginBottom: 24,
                  textAlign: "center",
                }}
              >
                {selectedPackage?.priceLabel}
              </MediumFont>
              <Register
                selectedPackage={selectedPackage}
                onRegisterSuccess={handleRegisterSbsSuccess}
              />
              <button
                type="button"
                onClick={() => setShowRegister(false)}
                style={{
                  width: "100%",
                  marginTop: 16,
                  padding: "10px 28px",
                  border: `1px solid ${COLORS.border}`,
                  borderRadius: 10,
                  background: "transparent",
                  color: COLORS.textSecondary,
                  fontFamily: "hebM",
                  fontSize: 14,
                  cursor: "pointer",
                }}
              >
                סגור
              </button>
            </div>
          }
          init={() => setShowRegister(false)}
        />
      )}

      {/* פופאפ חגיגי אחרי הרשמה מוצלחת (כמו MainUnAuthDIY) — מחוץ ל-GenericModal כדי שלא ייעלם בסגירת הרשמה */}
      {welcomePopupOpen && (
        <>
          <div
            style={sbsWelcomeBackdrop}
            onClick={() => setWelcomePopupOpen(false)}
            aria-hidden="true"
          />
          <div
            style={sbsWelcomeBox}
            role="dialog"
            aria-labelledby="sbs-welcome-popup-title"
          >
            <h2 id="sbs-welcome-popup-title" style={sbsWelcomeTitle}>
              ברוכים הבאים!
            </h2>
            <p style={sbsWelcomeIntro}>פרטי התחברות למערכת:</p>
            <div style={sbsWelcomeDetails}>
              <p style={sbsWelcomeRow}>
                <strong>לינק:</strong>{" "}
                <a
                  href={SBS_LOGIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  style={sbsWelcomeLink}
                >
                  {SBS_LOGIN_URL}
                </a>
              </p>
              <p style={sbsWelcomeRow}>
                <strong>טלפון:</strong> {welcomePhone}
              </p>
              <p style={sbsWelcomeRow}>
                <strong>סיסמא:</strong> {welcomePassword}
              </p>
            </div>
            <button
              type="button"
              style={sbsWelcomeCopyBtn}
              onClick={() =>
                copyToClipBoard(
                  `לינק: ${SBS_LOGIN_URL}\nטלפון: ${welcomePhone}\nסיסמא: ${welcomePassword}`,
                )
              }
            >
              לחצו כאן להעתקת פרטי ההתחברות
            </button>
            <p style={sbsWelcomeNote}>
              ניתן להתחבר ממספר מכשירים במקביל עם אותם פרטי התחברות
            </p>
            <p style={sbsWelcomeCheers}>בהצלחה 🙂</p>
            <button
              type="button"
              style={sbsWelcomeCloseBtn}
              onClick={() => setWelcomePopupOpen(false)}
            >
              סגור
            </button>
          </div>
        </>
      )}
    </section>
  );
};

// ——— סקשן 3: שאלות ותשובות (5–10) ———
const SectionFAQ = () => {
  return (
    <section
      style={{
        background: COLORS.heroGradient,
        padding: "64px clamp(16px, 5vw, 32px) 80px",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <BoldFont
        style={{
          fontSize: "clamp(20px, 5vw, 24px)",
          color: "#ffffff",
          textAlign: "center",
          marginBottom: 24,
        }}
      >
        שאלות ותשובות
      </BoldFont>
      <MediumFont
        style={{
          fontSize: 17,
          color: "rgba(255,255,255,0.92)",
          textAlign: "center",
          marginBottom: 32,
          lineHeight: 1.6,
        }}
      >
        ישנו מסמך תיאום ציפיות ובו כל השאלות והתשובות
      </MediumFont>
      <div style={{ display: "flex", justifyContent: "center" }}>
        <a
          href="https://wedding-harmony-2587b071.base44.app/WeddingOnboarding"
          target="_blank"
          rel="noopener noreferrer"
          style={{
            padding: "14px 32px",
            borderRadius: 12,
            border: "none",
            background: "#ffffff",
            color: COLORS.heroDark,
            fontSize: 16,
            fontFamily: "hebM",
            cursor: "pointer",
            textDecoration: "none",
            boxShadow: "0 4px 16px rgba(0,0,0,0.2)",
            transition: "transform 0.2s, box-shadow 0.2s",
          }}
        >
          למסמך תיאום ציפיות
        </a>
      </div>
    </section>
  );
};

// ——— סקשן 4: פוטר ———
const SectionFooter = () => {
  return (
    <footer
      style={{
        background: COLORS.bgSection,
        padding: "32px clamp(16px, 5vw, 32px)",
        borderTop: `1px solid ${COLORS.border}`,
        textAlign: "center",
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <MediumFont
        style={{
          fontSize: 14,
          color: COLORS.textSecondary,
        }}
      >
        שטרית איתי רם - פיתוח מערכות
      </MediumFont>
    </footer>
  );
};

const loginStyles = {
  loginTitle: {
    color: "#2d2855",
    fontSize: 18,
    fontWeight: 600,
    textAlign: "center",
    marginBottom: 12,
    fontFamily: "'Heebo', sans-serif",
  },
  form: {
    display: "flex",
    flexDirection: "column",
    gap: 8,
  },
  inputGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  label: {
    color: "#2d2855",
    fontSize: 13,
    fontWeight: 500,
    fontFamily: "'Heebo', sans-serif",
  },
  input: {
    background: "#f8f8f9",
    border: "1px solid #e8e8ec",
    borderRadius: 10,
    padding: "10px 14px",
    fontSize: 15,
    color: "#2d2855",
    outline: "none",
    transition: "all 0.2s ease",
    fontFamily: "'Heebo', sans-serif",
    direction: "rtl",
  },
  submitButton: {
    background:
      "linear-gradient(135deg, rgb(48, 43, 99), rgba(68, 59, 149, 0.55))",
    border: "none",
    borderRadius: 10,
    padding: "10px",
    fontSize: 15,
    fontWeight: 600,
    color: "#ffffff",
    cursor: "pointer",
    transition: "all 0.2s ease",
    marginTop: 4,
    fontFamily: "'Heebo', sans-serif",
    boxShadow: "0 2px 12px rgba(26, 26, 62, 0.35)",
  },
};

const MainUnAuthSBS = () => {
  const dispatch = useDispatch();
  const { todosLoading } = useSelector((state) => state.todosReducer);
  const [isLoading, setIsLoading] = useState(false);
  const [showLogin, setShowLogin] = useState(false);

  useEffect(() => {
    localStorage.setItem(DEFAULT_UNAUTH_STORAGE_KEY, "mainSBS");
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  const onSubmit = async (data) => {
    setIsLoading(true);
    const body = { ...data };
    dispatch(login(body)).then((res) => {
      setIsLoading(false);
      if (!res.payload?.error) {
        toast.success("הפעולה בוצעה בהצלחה");
      } else toast.error(res.payload?.error);
    });
  };

  return (
    <div
      style={{
        background: COLORS.bg,
        minHeight: "100vh",
        direction: "rtl",
        position: "relative",
        overflow: "hidden",
        width: "100%",
        alignSelf: "stretch",
        boxSizing: "border-box",
      }}
    >
      {/* כניסה למערכת */}
      <button
        type="button"
        onClick={() => setShowLogin(true)}
        title="התחברות"
        aria-label="התחברות"
        style={{
          position: "fixed",
          top: 16,
          left: 16,
          zIndex: 10,
          direction: "ltr",
          height: 44,
          padding: "0 14px 0 12px",
          display: "flex",
          flexDirection: "row",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          border: `1px solid ${COLORS.accentLight}`,
          borderRadius: 22,
          background: COLORS.bgSection,
          cursor: "pointer",
          boxShadow: `0 2px 10px ${COLORS.accent}20`,
        }}
      >
        <img
          src={personIcon}
          alt=""
          style={{
            width: 24,
            height: 24,
            opacity: 0.9,
            flexShrink: 0,
          }}
        />
        <MediumFont
          style={{
            fontSize: 15,
            fontWeight: 600,
            color: COLORS.text,
            whiteSpace: "nowrap",
            direction: "rtl",
          }}
        >
          התחברות
        </MediumFont>
      </button>

      {/* כפתור טלפון צף */}
      <a
        href="tel:0547439322"
        title="התקשרו אלינו"
        aria-label="התקשרו אלינו"
        style={{
          position: "fixed",
          bottom: 20,
          left: 16,
          zIndex: 10,
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 16px",
          border: `1px solid ${COLORS.border}`,
          borderRadius: 50,
          background: COLORS.bgSection,
          cursor: "pointer",
          boxShadow: `0 4px 16px ${COLORS.accent}25`,
          textDecoration: "none",
        }}
      >
        <MediumFont
          style={{
            fontSize: 15,
            color: COLORS.text,
            direction: "ltr",
          }}
        >
          0547439322
        </MediumFont>
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <path
            d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"
            fill={COLORS.accentLight}
          />
        </svg>
      </a>

      <SectionHero />
      <SectionPackages />
      <SectionFAQ />
      <SectionFooter />

      {(todosLoading || isLoading) && <OpacityLoading />}

      {showLogin && (
        <GenericModal
          div={
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                background: COLORS.bgSection,
                padding: "32px 24px",
                borderRadius: 20,
                maxWidth: "min(380px, calc(100vw - 32px))",
                width: "100%",
                boxShadow: `0 12px 40px ${COLORS.accent}30`,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
              }}
            >
              <h3 style={loginStyles.loginTitle}>כניסה למערכת</h3>

              <form
                onSubmit={handleSubmit(onSubmit)}
                style={{
                  ...loginStyles.form,
                  width: "100%",
                }}
              >
                <div style={loginStyles.inputGroup}>
                  <label style={loginStyles.label}>מספר נייד</label>
                  <input
                    style={loginStyles.input}
                    placeholder="050XXXXXXX"
                    {...register("phone", {
                      required: true,
                      pattern: /^[0]{1}[5]{1}[0-9]{8}$/,
                    })}
                  />
                  {CFV("phone", "פורמט מספר פלאפון לא חוקי", errors)}
                </div>

                <div style={loginStyles.inputGroup}>
                  <label style={loginStyles.label}>סיסמא</label>
                  <input
                    style={loginStyles.input}
                    type="password"
                    placeholder="הזן סיסמא"
                    {...register("password", {
                      required: true,
                      minLength: 2,
                      maxLength: 20,
                    })}
                  />
                  {CFV("password", "בין 2 ל 20 תווים", errors)}
                </div>

                <button type="submit" style={loginStyles.submitButton}>
                  כניסה למערכת
                </button>
              </form>
            </div>
          }
          init={() => setShowLogin(false)}
        />
      )}
    </div>
  );
};

export default MainUnAuthSBS;
